import { ChipFilters, TabFilter } from '@/features/shared/ElasticComponent';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import FiltersPopover from '@/features/shared/Filters';
import {
  datalayerPush,
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { MuiButton, MuiInputAdornment, MuiTextField } from '@/features/shared/material-ui';
import { ONE_TRUST_COOKIE_ACCEPT_CODE } from '@/hooks/useDataLayerEvent';
import { ColorNames, LoadingStatusValues, Theme } from '@/utils/constants';
import { waitForCriteria } from '@/utils/wait-for-criteria';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';
import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';
import useStyles, { useSearchBarStyles } from './SearchBar.styles';

const SearchBar = React.forwardRef(
  ({ theme = Theme.light, labels, config = {} }: any, ref?: React.Ref<HTMLDivElement>) => {
    const classes = useStyles({});
    const searchBarClasses = useSearchBarStyles();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [dataLayerId, setDataLayerId] = React.useState('');
    const isRevision = useIsRevision();
    const [isFirstLoad, setIsFirstLoad] = React.useState(true);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState<boolean>(Boolean(anchorEl));

    const {
      queryParams,
      updateQueryParams,
      forceUpdate = () => null,
      pagination,
      status,
    } = useContext(ElasticComponentContext);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [input, setInput] = useState(queryParams.q);

    const handleScroll = useCallback(() => {
      const el = anchorEl || popoverRef.current || undefined;
      const elHeight = el?.clientHeight || 0;
      if (!open && elHeight > window.screenTop) {
        setOpen(false);
        setAnchorEl(null);
      }

      return () => setOpen(false);
    }, [anchorEl, open, popoverRef]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const setSearchQuery = React.useCallback(() => {
      updateQueryParams('q', input);
      updateQueryParams('page', '1');

      if (input) {
        updateQueryParams('sortBy', '_score');
        updateQueryParams('sortType', 'desc');
      }
    }, [input, updateQueryParams]);

    const submitQuery = () => {
      setSearchQuery();
      setDataLayerId('search');
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    // Close and open modal.
    useEffect(() => {
      setOpen(Boolean(anchorEl));
    }, [anchorEl]);

    // Submits works with Filters.
    const handleSubmit = () => {
      setSearchQuery();
      setDataLayerId('apply_filters');
    };

    React.useEffect(() => {
      if (status !== LoadingStatusValues.loading) return;
      if (dataLayerId !== 'apply_filters') return;

      const filters = queryParams?.filters;
      const searchTerm = queryParams?.q?.trim();
      const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
      const searchFilters = getDataLayerSearchFilters(filters);

      const dataLayer = {
        event: 'trackEvent-filterSearchResult',
        event_name: 'filter_search_results',
        ...(!!searchTerm && { search_term: searchTerm }),
        num_search_result: pagination?.totalResults || 0,
        index_num: String(pagination?.currentPage || 1),
        is_revision: isRevision,
        ...(!!searchFilters && { search_filters: searchFilters }),
        num_search_filters: getDataLayerNumFilters(filters),
        ...searchFiltersObject,
      };
      TagManager.dataLayer({ dataLayer });

      setDataLayerId('');
    }, [
      dataLayerId,
      isRevision,
      pagination?.currentPage,
      pagination?.totalResults,
      queryParams?.filters,
      queryParams?.q,
      status,
    ]);

    React.useEffect(() => {
      if (dataLayerId !== 'search') return;
      if (isFirstLoad && status !== LoadingStatusValues.ready) return;
      if (!isFirstLoad && status !== LoadingStatusValues.loading) return;

      const filters = queryParams?.filters;
      const searchTerm = queryParams?.q;
      const variantName = (
        wrapperRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement
      )?.dataset?.datalayerVariant;

      waitForCriteria({
        criteria: () => window?.OnetrustActiveGroups?.includes(ONE_TRUST_COOKIE_ACCEPT_CODE),
        callback: () => {
          datalayerPush({
            compRef: wrapperRef,
            args: {
              event: 'searchQuery',
              revision: isRevision,
              params: { filters, q: searchTerm },
              variantName,
            },
            event: 'gtm:custom:sdl',
          });

          datalayerPush({
            compRef: wrapperRef,
            event: 'gtm:sdl:search',
            args: {
              search_term: searchTerm,
              num_search_result: pagination?.totalResults || 0,
              index_num: String(pagination?.currentPage || 1),
              is_revision: isRevision,
              filters,
              sdl_category: queryParams?.category_type,
            },
          });
        },
      });

      setDataLayerId('');
      setIsFirstLoad(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, dataLayerId, isFirstLoad]);

    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
      setInput(e.target.value);
    };

    const handlePress = (event: KeyboardEvent<HTMLInputElement>) => {
      const isEnter = /^enter$/i.test(event.key);
      if (!isEnter) return;

      setSearchQuery();
      setDataLayerId('search');
    };

    React.useEffect(() => {
      if (!queryParams?.q) return;

      setDataLayerId('search');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rootClasses = clsx(classes.wrapper, `${theme}-theme`, 'gntc-search-bar');

    useEffect(() => {
      document.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();

      return () => {
        document.removeEventListener('scroll', handleScroll);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
      <>
        <div className={rootClasses} ref={wrapperRef}>
          <div className={classes.inputWrapper}>
            <MuiTextField
              fullWidth
              onChange={handleInput}
              onKeyPress={handlePress}
              value={input || ''}
              InputProps={{
                placeholder: labels?.search || 'Search',
                classes: searchBarClasses,
                startAdornment: (
                  <MuiInputAdornment position="start" className={classes.icon}>
                    <SearchIcon className={classes.iconImage} />
                  </MuiInputAdornment>
                ),
                disableUnderline: true,
              }}
            />
            <MuiButton className={classes.buttonMobile} color="secondary" onClick={submitQuery}>
              {labels?.search || 'Search'}
            </MuiButton>
          </div>
          {!isMobile && (
            <FiltersPopover
              ref={popoverRef}
              config={config}
              isSorted
              theme={theme}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              onSubmit={handleSubmit}
              facetsSearch={JSON.parse(config?.facets_search || '[]')}
              hasScroll
            />
          )}
          <span className={classes.mobile}>
            <MuiButton
              className={classes.button}
              variant="contained"
              color="secondary"
              onClick={handleClick}
              data-datalayer-ignore-cta
            >
              {labels?.apply_filters || 'Apply Filters'} +
            </MuiButton>
          </span>
          {isMobile && (
            <div className={classes.mobileBar}>
              <TabFilter
                theme={Theme.dark}
                onChange={handleSubmit}
                isSorted
                size="small"
                labels={labels}
              />
            </div>
          )}
        </div>
        <div className={classes.chips}>
          <ChipFilters
            chipColor={ColorNames.charcoal}
            hoverable={false}
            theme={Theme.dark}
            onDelete={() => {
              forceUpdate();
            }}
          />
        </div>
      </>
    );
  },
);

export default memo(SearchBar);
